import React from "react";
import { Link } from "react-router-dom";

// Static preview: a system overview banner above a row of its component
// tiles. No rotation, no blur, no independent floating — decorative only.

/* Le systeme reellement publie au catalogue, avec ses vraies pieces et son
   vrai prix. La maquette precedente presentait un "Sales Operating System"
   a 499 € qui n'existe nulle part : un visiteur pouvait le chercher. */
const FEATURES = [
  "Grille de qualification notée sur 100",
  "Structure de message en sept temps",
  "Protocole de relance à J+6",
  "Journal de suivi et ses indicateurs",
  "Mise en place en trois heures",
];

const ITEMS = [
  { id: "methode", category: "Méthode", name: "Procédure écrite", status: "Incluse", gradient: "linear-gradient(155deg,#0d1420,#05070a)" },
  { id: "qualification", category: "Qualification", name: "Grille notée", status: "Incluse", gradient: "linear-gradient(155deg,#17131d,#08070b)" },
  { id: "message", category: "Message", name: "Structure en sept temps", status: "Incluse", gradient: "linear-gradient(155deg,#10160f,#060906)" },
  { id: "relance", category: "Relance", name: "Protocole à J+6", status: "Inclus", gradient: "linear-gradient(155deg,#151515,#070707)" },
  { id: "suivi", category: "Suivi", name: "Journal et indicateurs", status: "Inclus", gradient: "linear-gradient(155deg,#18130c,#0a0704)" },
  { id: "outils", category: "Outils", name: "Messagerie et tableur", status: "Requis", gradient: "linear-gradient(155deg,#101820,#05090c)" },
];

const CHECK_PATH = "M5 12.5l4 4 10-10";

const ICON_PATHS = {
  crm: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="10" r="1.6" /><path d="M6 16c0-1.5 1.1-2.5 2.5-2.5s2.5 1 2.5 2.5" /><path d="M14 9h4M14 13h4" /></>,
  "agent-ia": <path d="M21 11.5a8.38 8.38 0 01-4.5 7.45 8.38 8.38 0 01-8.9-.4L3 20l1.45-4.6a8.38 8.38 0 01-.45-2.9 8.5 8.5 0 018.5-8.5h.5a8.48 8.48 0 018 8v.5z" />,
  prospection: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>,
  paiement: <><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></>,
  documentation: <><path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6M9 17h6" /></>,
  affiliation: <><circle cx="18" cy="5" r="2.6" /><circle cx="6" cy="12" r="2.6" /><circle cx="18" cy="19" r="2.6" /><path d="M8.4 10.6l7.2-4.2M8.4 13.4l7.2 4.2" /></>,
};

const CSS = `
  @keyframes tg-scene-reveal { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:none; } }

  .tg-scene-outer {
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
  }

  .tg-scene-root {
    position: relative;
    width: 100%;
    box-sizing: border-box;
    padding: 40px clamp(20px, 4vw, 56px) 36px;
    border-radius: 32px;
    /* Card blanche opaque posee sur la section grise #F5F5F7. */
    background: #FFFFFF;
    border: 1px solid rgba(0,0,0,0.06);
    box-shadow: 0 30px 80px -34px rgba(0,0,0,0.18);
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  .tg-scene-hero {
    position: relative;
    width: 100%;
    box-sizing: border-box;
    padding: 0;
    /* Pas de card dans la card : le bloc vit a plat sur le blanc,
       la hierarchie se joue uniquement sur les hairlines. */
    background: transparent;
    border: 0;
    box-shadow: none;
    display: flex;
    flex-direction: column;
    gap: 20px;
    animation: tg-scene-reveal 0.9s cubic-bezier(0.28,0.11,0.32,1) both;
  }
  .tg-scene-hero-title { font-size: clamp(24px, 3vw, 36px); font-weight: 700; letter-spacing: -0.02em; color: #1D1D1F; }
  .tg-scene-hero-sub { margin-top: 8px; font-size: 15px; color: #6E6E73; }

  .tg-scene-hero-divider { height: 1px; background: rgba(0,0,0,0.10); }

  .tg-scene-hero-features {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 28px;
  }
  .tg-scene-hero-feature { display: flex; align-items: center; gap: 9px; font-size: 14px; color: rgba(0,0,0,0.80); }
  .tg-scene-hero-feature svg { flex-shrink: 0; }

  .tg-scene-hero-buy { margin-top: 4px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .tg-scene-mono { font-family: "ui-monospace", "SFMono-Regular", "Menlo", monospace; }
  .tg-scene-hero-price { font-size: 26px; font-weight: 600; letter-spacing: -0.02em; color: #1D1D1F; }
  .tg-scene-hero-cta {
    display: inline-flex; align-items: center; gap: 8px; padding: 11px 22px; border-radius: 980px;
    background: #ffffff; border: 1px solid #1D5FD1; text-decoration: none;
  }
  .tg-scene-hero-cta-text { font-size: 14px; font-weight: 600; color: #1D5FD1; letter-spacing: -0.01em; }

  .tg-scene-row-label {
    font-size: 15px;
    font-weight: 600;
    color: #1D1D1F;
    /* Separe le bloc "offre" de l'inventaire, a la place de l'ancien panneau gris. */
    border-top: 1px solid rgba(0,0,0,0.10);
    padding-top: 24px;
  }

  .tg-scene-row {
    display: flex;
    gap: 14px;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .tg-scene-row::-webkit-scrollbar { display: none; }

  .tg-scene-card {
    position: relative;
    flex: 0 0 auto;
    width: 200px;
    aspect-ratio: 16 / 10;
    border-radius: 10px;
    overflow: hidden;
    /* Tuile opaque, gris tres clair sur la card blanche. */
    background: #F7F7F8;
    border: 1px solid rgba(0,0,0,0.07);
    box-shadow: 0 8px 22px -12px rgba(0,0,0,0.12);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    animation: tg-scene-reveal 0.8s cubic-bezier(0.28,0.11,0.32,1) both;
  }
  .tg-scene-card:hover {
    transform: scale(1.035);
    box-shadow: 0 20px 40px -14px rgba(0,0,0,0.28);
  }
  .tg-scene-card-top {
    position: absolute; top: 12px; left: 12px;
    display: flex; align-items: center; gap: 8px;
  }
  .tg-scene-card-icon { display: flex; color: rgba(0,0,0,0.62); flex-shrink: 0; }
  .tg-scene-card-eyebrow {
    font-size: 17.5px; font-weight: 600; letter-spacing: -0.01em; text-transform: none; color: rgba(0,0,0,0.50);
  }
  .tg-scene-card-content { position: absolute; left: 12px; right: 12px; bottom: 10px; }
  .tg-scene-card-name { font-size: 16px; font-weight: 700; letter-spacing: -0.01em; color: #1D1D1F; }
  .tg-scene-card-status { margin-top: 5px; display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 600; color: rgba(0,0,0,0.60); }
  .tg-scene-card-status svg { flex-shrink: 0; }

  @media (max-width: 768px) {
    .tg-scene-hero-features { grid-template-columns: 1fr; }
    .tg-scene-card { width: 168px; }
  }
`;

function Check({ size = 13, stroke = "#FF6B00" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d={CHECK_PATH} />
    </svg>
  );
}

function Card({ item, index }) {
  return (
    <div
      className="tg-scene-card"
      style={{ animationDelay: `${0.15 + index * 0.06}s` }}
    >
      <div className="tg-scene-card-top">
        <span className="tg-scene-card-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            {ICON_PATHS[item.id]}
          </svg>
        </span>
        <span className="tg-scene-card-eyebrow">{item.category}</span>
      </div>
      <div className="tg-scene-card-content">
        <div className="tg-scene-card-name">{item.name}</div>
        <div className="tg-scene-card-status">
          <Check size={12} />
          <span>{item.status}</span>
        </div>
      </div>
    </div>
  );
}

export default function SceneVivante() {
  return (
    <>
      <style>{CSS}</style>
      <div className="tg-scene-outer">
        <div className="tg-scene-root">
          <div className="tg-scene-hero">
            <div>
              <div className="tg-scene-hero-title">Vingt experts contactés, un par un</div>
              <div className="tg-scene-hero-sub">Identifier vingt interlocuteurs difficiles à joindre et les contacter individuellement, en quatre semaines.</div>
            </div>

            <div className="tg-scene-hero-divider" />

            <div className="tg-scene-hero-features">
              {FEATURES.map((f) => (
                <div className="tg-scene-hero-feature" key={f}>
                  <Check />
                  {f}
                </div>
              ))}
            </div>

            <div className="tg-scene-hero-buy">
              <div className="tg-scene-mono tg-scene-hero-price">Gratuit</div>
              <Link to="/marketplace" className="tg-scene-hero-cta">
                <span className="tg-scene-hero-cta-text">Découvrir</span>
              </Link>
            </div>
          </div>

          <div className="tg-scene-row-label">Ce que contient le système.</div>

          <div className="tg-scene-row">
            {ITEMS.map((item, index) => (
              <Card key={item.id} item={item} index={index} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
